<script setup lang="ts">
// Generic sign-rule Signal tab (Miners fund pack, Phase 1C 2026-10-25): AmatiSignalTab generalised.
// A fund on the config-driven fund pack strikes a BUY/SELL decision at 08:40 London off its reference
// futures' move since the previous exchange close (Config/fund_packs/<slug>.json signal.*). The producer's
// latest:signal:<fund> payload is read verbatim via GET /api/signal?fund=<slug> (api.gatedSignal; 404 unless
// the caller may view the fund).
//
// Everything fund-specific comes from FUND_REGISTRY[slug]: label, fund discriminator, signalRoot (the futures
// symbol, e.g. 'GC') and signalAnchorLabel (e.g. 'NY close'). No K_t panel — the fund has none yet. A 404 /
// failed fetch / `available:false` / degraded payload all render the same calm "No live signal yet" state —
// never an alert, never a throw. `ctx` is accepted (uniform signalTab contract) and ignored: this tab polls
// its own data, like AmatiSignalTab.
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { api, type GatedFuturesSignal } from '../../api/client'
import type { SignalTabCtx } from '../nav/fundPages'
import { FUND_REGISTRY, type PortfolioSlug } from '../nav/fundConfig'
import FuturesMoveCard from './FuturesMoveCard.vue'
import { formatSignedBp } from '../japan/japanReasons'

const props = defineProps<{ ctx: SignalTabCtx; slug: PortfolioSlug }>()

const SIGNAL_POLL_MS = 60_000

const entry = computed(() => FUND_REGISTRY[props.slug])
const label = computed(() => entry.value.label)
const root = computed(() => entry.value.signalRoot ?? 'futures')
const anchorLabel = computed(() => entry.value.signalAnchorLabel ?? 'previous close')

const signal = ref<GatedFuturesSignal | null>(null)

/** A usable live decision: present, not degraded, BUY or SELL. */
const live = computed<GatedFuturesSignal | null>(() => {
  const s = signal.value
  if (!s || s.available === false || s.degraded === true) return null
  if (s.decision !== 'BUY' && s.decision !== 'SELL') return null
  return s
})

const LONDON_DATE = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit',
})
const LONDON_HHMM = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
})
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const todayLondon = computed(() => LONDON_DATE.format(new Date()))

const moveLabel = computed(() => {
  const m = live.value?.m_0840
  return m == null ? '—' : `${formatSignedBp(m)} bp`
})

const asOfLabel = computed(() => {
  const ts = live.value?.timestamp
  if (!ts) return null
  const d = new Date(ts)
  if (Number.isNaN(d.getTime())) return null
  const day = LONDON_DATE.format(d)
  const hhmm = `${LONDON_HHMM.format(d)} London`
  if (day === todayLondon.value) return `Published ${hhmm} today`
  const [, mo, dd] = day.split('-').map(Number)
  return `Published ${hhmm} on ${dd} ${MONTHS[mo - 1]}`
})

/** FuturesMoveCard's reason line — only ever called for a healthy payload. */
function describeMove(s: GatedFuturesSignal): { text: string; tone: 'ok' | 'amber' } {
  if (s.m_0840 == null) return { text: `${root.value} move unavailable`, tone: 'ok' }
  const t = Math.round(s.threshold_bps ?? 0)
  const side = s.m_0840 * 10000 >= t ? 'at or above' : 'below'
  return { text: `${root.value} ${formatSignedBp(s.m_0840)} bp since ${anchorLabel.value} — ${side} the ${t} bp threshold`, tone: 'ok' }
}

async function pollSignal() {
  try {
    signal.value = await api.gatedSignal(entry.value.fund)
  } catch {
    // 404 (not viewable / not yet published) or a network blip: calm empty state.
    signal.value = null
  }
}

let timer: ReturnType<typeof setInterval> | null = null
onMounted(() => {
  pollSignal()
  timer = setInterval(pollSignal, SIGNAL_POLL_MS)
})
onUnmounted(() => {
  if (timer) clearInterval(timer)
})
</script>

<template>
  <div class="card signrule-verdict" data-testid="signrule-verdict">
    <div class="sr-head">
      <span class="sr-title">{{ label }} · 08:40 London decision</span>
      <span class="sr-sub">{{ root }} since the previous {{ anchorLabel }}</span>
    </div>
    <template v-if="live">
      <div class="sr-row">
        <span class="sr-decision" :class="live.decision === 'BUY' ? 'sr-buy' : 'sr-sell'" data-testid="signrule-decision">
          {{ live.decision }}
        </span>
        <span class="sr-move" data-testid="signrule-move">{{ root }} {{ moveLabel }}</span>
      </div>
      <div v-if="asOfLabel" class="sr-asof" data-testid="signrule-asof">{{ asOfLabel }}</div>
    </template>
    <div v-else class="sr-empty" data-testid="signrule-no-signal">
      No live signal yet — the next decision is at 08:40 London
    </div>
  </div>
  <FuturesMoveCard
    v-if="live"
    :signal="live"
    :today-london="todayLondon"
    :symbol="root"
    :anchor-label="anchorLabel"
    :test-id="`${slug}-move-card`"
    :describe="describeMove"
  />
</template>

<style scoped>
.signrule-verdict { display: flex; flex-direction: column; gap: 6px; }
.sr-head { display: flex; flex-direction: column; gap: 2px; }
.sr-title { font-size: 0.9rem; font-weight: 600; color: #e6e6ef; }
.sr-sub { font-size: 0.76rem; color: #8b949e; }
.sr-row { display: flex; align-items: baseline; gap: 12px; flex-wrap: wrap; }
.sr-decision { font-size: 1.6rem; font-weight: 700; letter-spacing: 0.02em; }
.sr-buy { color: #3fb950; }
.sr-sell { color: #f85149; }
.sr-move { font-size: 0.95rem; font-variant-numeric: tabular-nums; color: #c9d1d9; }
.sr-asof { font-size: 0.78rem; color: #8b949e; }
.sr-empty { font-size: 0.85rem; color: #8b949e; padding: 4px 0; }
</style>
